/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useWorldMapColors.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the geoFirewall module.
────────────────────────────────────────────────────────────────*/


import { useEffect, RefObject, Dispatch, SetStateAction } from 'react';

const SELECTED_COLOR = '#dc2626';
const DEFAULT_COLOR = '#22c55e';

interface TooltipState { name: string; x: number; y: number; visible: boolean; }

export function useWorldMapColors(
    selectedCountries: string[],
    mapLibLoaded: boolean,
    mapInstanceRef: RefObject<any>,
    mapContainerRef: RefObject<HTMLDivElement>,
    eventListenersRef: RefObject<Map<SVGPathElement, () => void>>,
    setTooltip: Dispatch<SetStateAction<TooltipState>>,
) {
    useEffect(() => {
        if (!mapInstanceRef.current || !mapContainerRef.current) return;
        const container = mapContainerRef.current;
        const selectedSet = new Set(
            selectedCountries.filter((c) => c && typeof c === 'string').map((c) => c.toLowerCase())
        );

        const updateColors = () => {
            const allPaths = container.querySelectorAll<SVGPathElement>('path.jvm-region');
            if (allPaths.length === 0) { setTimeout(updateColors, 100); return; }
            allPaths.forEach((pathEl) => {
                const code = pathEl.getAttribute('data-code');
                if (!code) return;
                const color = selectedSet.has(code.toLowerCase()) ? SELECTED_COLOR : DEFAULT_COLOR;
                pathEl.style.setProperty('fill', color, 'important');
                pathEl.setAttribute('fill', color);
                if (!eventListenersRef.current!.has(pathEl)) {
                    const hide = () => setTooltip((prev) => ({ ...prev, visible: false }));
                    pathEl.addEventListener('mouseleave', hide);
                    eventListenersRef.current!.set(pathEl, hide);
                }
            });
        };

        updateColors();
        const t1 = setTimeout(updateColors, 200);
        const t2 = setTimeout(updateColors, 500);
        const t3 = setTimeout(updateColors, 1000);

        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            eventListenersRef.current!.forEach((h, el) => el.removeEventListener('mouseleave', h));
            eventListenersRef.current!.clear();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountries, mapLibLoaded]);
}
